import { useEffect, useMemo, useState } from "react";
import { Combobox, Checkbox } from "@bst/react-ui";
import { useTranslation } from "react-i18next";

type VisibleColumn = {
  id: string;
  title: string;
  visible: boolean;
  columnKey: string;
};

type Props = {
  show: boolean;
  columns: VisibleColumn[];
  menuPosition: {
    top: number;
    left: number;
  };
  setShow: (value: boolean) => void;
  handleColumnVisibility: (item: VisibleColumn) => void;
};

const NO_RESULT = "NO_RESULT"

export const ColumnFilterMenu = ({
  show,
  columns,
  menuPosition,
  setShow,
  handleColumnVisibility,
}: Props) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  useEffect(() => {
    // Hide column menu on resize
    const grid = document.querySelector('.rdg.user-management-grid')
    if (!grid) {
      return
    }
    const resizeObserver = new ResizeObserver(() => setShow(false))
    resizeObserver.observe(grid)
    return () => {
      resizeObserver.unobserve(grid)
      resizeObserver.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Return columns filtered by search value
  const filteredColumns = useMemo(() => {
    if (search === "") {
      return columns;
    }
    const results = columns.filter((item) =>
      item.title.toLowerCase().includes(search.toLowerCase())
    )
    if (results.length) {
      return results;
    }
    return [{
      id: NO_RESULT,
      title: NO_RESULT,
      visible: true
    }]
  }, [columns, search])
    .sort((a, b) => a.title.localeCompare(b.title));

  // Anchor element determines where to show Column Menu
  // It has dynamic position to reduce rendering Column Menu in each Header Cell
  const anchorElement = <span style={{ position: "fixed", ...menuPosition }}></span>

  const itemRenderer = (item: VisibleColumn) => {
    // No results view
    if (item.id === NO_RESULT) {
      return (
        <span
          data-testid="column-menu-no-results"
          className="user-management-column-menu__no-results"
          children={t('filter.no_results')}
        />
      )
    }
    // Column menu checkboxes
    return (
      <Checkbox
        key={item.id}
        data-testid={`column-menu-item-${item.id}`}
        label={item.title}
        checked={item.visible}
        onCheckedChange={(value) => {
          handleColumnVisibility({ ...item, visible: Boolean(value) });
        }}
      />
    )
  }

  const handleMenuToggle = (isShow: boolean) => {
    setShow(isShow)
    // If menu is closed clear search value
    if (!isShow) {
      setSearch("")
    }
  }

  return (
    <Combobox
      side="left"
      sideOffset={16}
      modal={true}
      className="user-management-column-menu"
      data-testid="column-menu"
      title={t("columnsMenu")}
      show={show}
      data={filteredColumns as VisibleColumn[]}
      search={search}
      anchorElement={anchorElement}
      setShow={handleMenuToggle}
      setSearch={setSearch}
      renderItem={itemRenderer}
    />
  );
};
